import React from 'react'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'

import s from './styles.module.scss'

import { Input } from './components/input/input'
import { TravelInputModeEnum } from './components/input/travel-input-mode.enum'
import { LocationPickerModeEnum } from './location-picker.enum'
import { LocationSearchBarPicker } from './LocationSearchBarPicker'
import Icon from '../../atoms/icons/icon'

type Handlers = {
  onEditing: (value: boolean) => void
  onSearchQuery: (value?: string) => void
  onLocationSelected: (value: boolean) => void
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

type State = {
  isEditing: boolean
  isLocationSelected: boolean
  isMobile: boolean
}

type Renderers = {
  renderInputField?: ({
    inputRef,
    onInputChange,
    onReset,
  }: {
    inputRef: React.LegacyRef<HTMLInputElement> | undefined
    onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onReset?: () => void
  }) => JSX.Element
  renderSearchBarPicker?: ({ value }: { value: string }) => JSX.Element
}

type LocationPickerInputFieldProps = {
  field: ControllerRenderProps<FieldValues, string>
  handlers: Handlers
  state: State
  renderers?: Renderers
  inputRef?: React.RefObject<HTMLInputElement>
  mode: LocationPickerModeEnum
  alternativeClassName?: string
  revampPicker: boolean
}

export const MapPointerIcon: React.FC<{ isLocationSelected: boolean }> = ({
  isLocationSelected,
}) => (
  <div className={s.mapPointerIcon}>
    <Icon
      name='map-pointer'
      size={20}
      color={isLocationSelected ? '#101F34' : '#A9A9A9'}
    />
  </div>
)

export const LocationPickerInputField: React.FC<
  LocationPickerInputFieldProps
> = ({
  field,
  state: { isEditing, isLocationSelected, isMobile },
  handlers: { onEditing, onSearchQuery, onLocationSelected, onInputChange },
  renderers,
  inputRef,
  mode,
  alternativeClassName,
  revampPicker,
}) => {
  const onReset = React.useCallback(() => {
    onSearchQuery(undefined)
    field.onChange(null)
    onLocationSelected(false)
    onEditing(false)
  }, [onSearchQuery, field, onLocationSelected, onEditing])

  const isHouseSearchMode = mode === LocationPickerModeEnum.HOUSE_SEARCH
  const searchBarPickerProps = {
    value: field.value?.label,
    isMobile,
    alternativeClassName,
  }

  if (isEditing && renderers?.renderInputField) {
    return renderers.renderInputField({ onInputChange, onReset, inputRef })
  }

  if (renderers?.renderSearchBarPicker) {
    return renderers.renderSearchBarPicker({ value: field.value?.label })
  }

  return (
    <div
      className={
        isMobile
          ? s.searchLocationInputContainerMobile
          : s.searchLocationInputContainer
      }
      data-testid={isHouseSearchMode ? 'location-input' : undefined}
    >
      <LocationSearchBarPicker
        mode={mode}
        isLocationSelected={isLocationSelected || !!field.value?.label}
        revampPicker={revampPicker}
        {...searchBarPickerProps}
      >
        <>
          {isEditing && (
            <Input
              onChange={onInputChange}
              onReset={onReset}
              ref={inputRef}
              mode={
                isHouseSearchMode ? TravelInputModeEnum.HOUSE_SEARCH : undefined
              }
            />
          )}
        </>
      </LocationSearchBarPicker>
    </div>
  )
}
